import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent, StripePaymentElementOptions } from '@stripe/stripe-js';
import classNames from 'classnames';
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  NanoCheckoutFailureReason,
  trackNanoCheckoutConfirmIncomplete,
  trackNanoCheckoutFailed,
  trackNanoCheckoutNotNowTapped,
  trackNanoCheckoutPayWithCardTapped,
  trackNanoCheckoutPaymentSubmitted,
  trackNanoCheckoutPurchased,
  trackNanoCheckoutWalletAvailable,
  trackNanoCheckoutWalletTapped,
} from '../../../lib/analytics/events';
import { ShippingAddress } from '../../../lib/checkout/address';
import { buildApplePayRecurringOption } from '../../../lib/checkout/applePay';
import { CardOnFile } from '../../../lib/checkout/useBillingPrefill';
import { isGwaPromoAlreadyRedeemed } from '../../../lib/checkout/gwaRedeemed';
import { CheckoutOffer } from '../../../lib/checkout/offer';
import { formatCents, PriceBreakdown } from '../../../lib/checkout/pricing';
import { customerMessageFromError, purchaseNanoUpsell } from '../../../lib/checkout/purchase';
import {
  CheckoutCustomerError,
  stripeCheckout,
  ThreeDSecureUnsupportedError,
} from '../../../lib/checkout/stripeCheckout';
import { Links } from '../../../lib/constants';
import styles from './CheckoutStep.module.scss';
import ExpressWalletButton from './ExpressWalletButton';
import { SHIP_TO_CARD_ID } from './ShipToCard';

export interface PaymentSectionProps {
  email: string;
  address: ShippingAddress;
  /** Stripe's own validation of the address currently in the element */
  addressComplete: boolean;
  /** the card already stored on the shopper's Recurly account, offered instead of the card form */
  cardOnFile: CardOnFile | null;
  offer: CheckoutOffer;
  breakdown: PriceBreakdown;
  petName: string;
  petId: string;
  gwa: boolean;
  onPurchased: () => void;
  /** the purchase was rejected because this email has already used the adoption offer */
  onOfferUsed: () => void;
  /** hand off to the shop for the cases nano cannot finish, notably a 3DS challenge */
  onFallbackToShop: () => void;
  /**
   * On desktop, the summary column's slot that the pay button, the renewal line and Not now render
   * into, so they sit with the total. Null on a phone, where they stay at the foot of the form with the
   * button in its sticky bar.
   */
  paySlot?: HTMLElement | null;
  /**
   * The 50/50 test (`nano-checkout-express-wallets`): Apple Pay or Google Pay as the pay bar's button,
   * where the sheet collects the address, rather than as options in the payment list.
   */
  expressWallets?: boolean;
}

/**
 * Smooth-scrolls a card to just under the sticky site header (its `scroll-margin-top`) and resolves
 * once the page has settled, so whatever runs next does not interrupt the scroll. Jumps straight
 * there with reduced motion, and resolves at once when the card is already in place.
 */
function smoothScrollTo(target: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    const offset = Number.parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    const alreadyThere = Math.abs(target.getBoundingClientRect().top - offset) < 2;
    const reducedMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (alreadyThere || reducedMotion) {
      target.scrollIntoView({ block: 'start' });
      resolve();
      return;
    }

    let settled = false;
    const done = () => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener('scrollend', done);
      window.clearTimeout(fallback);
      resolve();
    };
    window.addEventListener('scrollend', done);
    // browsers without `scrollend` (Safari before 18.2) settle on the timer instead
    const fallback = window.setTimeout(done, 900);
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

/**
 * The payment method list and the purchase itself.
 *
 * Must render inside `StripeElementsProvider` -- `useStripe`/`useElements` need the Elements context.
 */
export default function PaymentSection({
  email,
  address,
  addressComplete,
  cardOnFile,
  offer,
  breakdown,
  petName,
  petId,
  gwa,
  onPurchased,
  onOfferUsed,
  onFallbackToShop,
  paySlot = null,
  expressWallets = false,
}: PaymentSectionProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // where the last attempt came from, so its error is drawn beside the button the shopper used: the
  // wallet button's in the pay bar, the form's under the payment card
  const [errorFrom, setErrorFrom] = useState<'form' | 'wallet'>('form');
  // which list the shopper is paying from: the saved recurly card, or whatever is selected inside
  // the stripe element (new card, link, and apple pay or google pay unless they are in the wallet row)
  const [method, setMethod] = useState<'saved' | 'stripe'>(cardOnFile ? 'saved' : 'stripe');
  // the payment method type currently selected inside the stripe element, e.g. 'card' or 'apple_pay'
  const [stripeType, setStripeType] = useState<string | null>(null);
  const lastStripeType = useRef<string | null>(null);
  // stripe's own verdict on whether the selected method's inputs are filled in and valid
  const [elementComplete, setElementComplete] = useState(false);
  // an element that never loaded has no fields to complete, so it must not hold the button shut.
  // starts false so the gate is fully closed on mount -- there is no window where a click slips
  // through -- and flips only on a real load failure.
  const [elementFailed, setElementFailed] = useState(false);
  const paymentCardRef = useRef<HTMLDivElement>(null);

  const payingWithCardOnFile = !!cardOnFile && method === 'saved';

  /* A wallet in the payment list collects its payment in its own sheet, so there is nothing inline to
     complete. Per the shop's own Payment Element, `complete` is in fact reliable for wallets and Link,
     so this is a safety net rather than the thing protecting them -- it can only ever make the button
     more available, never less. Never true with the wallet row, which takes wallets out of the list. */
  const paysThroughWalletSheet = stripeType === 'apple_pay' || stripeType === 'google_pay';
  const paymentReady = payingWithCardOnFile || paysThroughWalletSheet || elementFailed || elementComplete;
  const canSubmit = addressComplete && paymentReady;
  // Apple Pay or Google Pay is on offer for the wallet button; false until Stripe has checked
  const [walletAvailable, setWalletAvailable] = useState(false);
  // The wallet button takes the pay bar whenever the form cannot pay in one tap yet: it is the quickest
  // way to pay, and the default. Once the address and card are complete (or a saved card is selected
  // with a complete address) Confirm is one tap too, and takes the bar back.
  const walletFirst = expressWallets && walletAvailable && !canSubmit;
  // `Wallet Shown` once per checkout: on desktop the pay block moves into the summary column's slot,
  // which remounts the wallet button and reports `ready` again
  const walletShownTracked = useRef(false);

  const paymentElementOptions: StripePaymentElementOptions = useMemo(() => {
    /* With the wallet row, Apple Pay and Google Pay live there, where the sheet collects the shipping
       address too; in this list they still need the typed address card complete before Confirm will
       open the sheet. */
    if (expressWallets) {
      return {
        layout: { type: 'accordion', radios: true, defaultCollapsed: false },
        paymentMethodOrder: ['card', 'link'],
        wallets: { applePay: 'never', googlePay: 'never' },
      };
    }
    /* every way to pay renders as one radio list: apple pay and google pay are options inside the
       Payment Element rather than an express button above it (an ExpressCheckoutElement on the page
       would suppress them here), and the card form starts expanded */
    // requests an apple pay merchant token (MPAN) so the wallet card can be charged off-session
    // when the membership renews. spread so it is absent entirely rather than `applePay: undefined`
    const applePayRecurring = buildApplePayRecurringOption(offer);
    return {
      layout: { type: 'accordion', radios: true, defaultCollapsed: false },
      // the card form leads; wallets follow rather than headline
      paymentMethodOrder: ['card', 'apple_pay', 'google_pay', 'link'],
      ...(applePayRecurring && { applePay: applePayRecurring }),
    };
  }, [expressWallets, offer]);

  const properties = { petId, gwa };

  const fail = (reason: NanoCheckoutFailureReason, message: string) => {
    trackNanoCheckoutFailed(properties, reason);
    setError(message);
  };

  /** A purchase that threw: the 3DS hand-off, the offer-used notice, or an inline error. */
  const handlePurchaseError = (err: unknown) => {
    if (err instanceof ThreeDSecureUnsupportedError) {
      trackNanoCheckoutFailed(properties, 'three_d_secure');
      onFallbackToShop();
      return;
    }
    if (err instanceof CheckoutCustomerError) {
      fail('card_declined', err.message);
      return;
    }
    // the shopper cannot retry their way past this one, so it is not an inline error: the notice
    // takes them to the shop at the regular price instead
    if (isGwaPromoAlreadyRedeemed(err)) {
      onOfferUsed();
      return;
    }
    // prefer whatever the server said it could tell the shopper: "that coupon has reached the
    // maximum number of redemptions on your account" is actionable, "please try again" is not.
    const serverMessage = customerMessageFromError(err);
    if (serverMessage) {
      fail('card_declined', serverMessage);
      return;
    }
    fail('service_error', 'Something went wrong taking that payment. Please try again.');
  };

  /**
   * Everything after a payment method has been read: save the shipping address (and store a new card),
   * then buy. Shared by the form's Confirm and the wallet button, so both get the same 3DS hand-off,
   * offer-used notice and inline errors. Null token is the saved-card path, where the card is already
   * stored. A wallet token is charged inline by the purchase instead of stored first.
   */
  const completePurchase = async (confirmationTokenId: string | null, shipTo: ShippingAddress, walletToken = false) => {
    try {
      await stripeCheckout(confirmationTokenId, email, shipTo, { chargedInline: walletToken });

      await purchaseNanoUpsell(
        offer,
        breakdown.shippingCode,
        walletToken ? (confirmationTokenId ?? undefined) : undefined,
      );

      trackNanoCheckoutPurchased(properties);
      onPurchased();
    } catch (err) {
      handlePurchaseError(err);
    }
  };

  /** The form's purchase: the saved card, or the card or Link in the element, with the address card. */
  const submit = async () => {
    if (!stripe || !elements || submitting) {
      return;
    }

    // Unreachable from the button, which routes an incomplete form to `showWhatIsMissing` instead, and
    // that is the point: this used to fire on a click and emit `address_error`, a failure the shopper
    // could not have avoided because nothing told them the form was short. Kept as a guard in case
    // `submit` ever gains another caller -- if this ever fires again, something bypassed the check.
    if (!addressComplete) {
      trackNanoCheckoutFailed(properties, 'address_error');
      setError('Please complete the shipping address above before confirming.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setErrorFrom('form');
    trackNanoCheckoutPaymentSubmitted(properties);

    try {
      if (payingWithCardOnFile) {
        await completePurchase(null, address);
        return;
      }

      // must run before createConfirmationToken, or stripe throws
      const submitResult = await elements.submit();
      if (submitResult.error) {
        fail('card_declined', submitResult.error.message ?? 'Check your card details and try again.');
        return;
      }

      // recurly confirms the SetupIntent with automatic payment methods and redirects allowed, so stripe
      // requires a return_url on the token itself; without one every method fails as a gateway api_error
      const { confirmationToken, error: tokenError } = await stripe.createConfirmationToken({
        elements,
        params: { return_url: `${window.location.origin}/register/checkout` },
      });
      if (tokenError || !confirmationToken) {
        // closing the wallet or link sheet is a decision, not a failure
        if (tokenError?.code === 'canceled') {
          return;
        }
        fail('card_declined', tokenError?.message ?? 'We could not read that card. Try another.');
        return;
      }

      await completePurchase(confirmationToken.id, address);
    } catch (err) {
      // elements.submit() or createConfirmationToken threw rather than returning an error
      handlePurchaseError(err);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * A wallet button was tapped, opening the sheet. `Payment Submitted` fires here rather than once the
   * sheet is authorized, so it counts the same moment it does in the payment list, where Confirm with a
   * wallet selected fires it before the sheet opens.
   */
  const onWalletTapped = () => {
    setError(null);
    setErrorFrom('wallet');
    trackNanoCheckoutWalletTapped(properties);
    trackNanoCheckoutPaymentSubmitted(properties);
  };

  /** The wallet sheet was authorized; the address is the one the sheet collected, not the address card. */
  const submitWallet = async (confirmationTokenId: string, shipTo: ShippingAddress) => {
    setSubmitting(true);
    try {
      await completePurchase(confirmationTokenId, shipTo, true);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Confirm tapped before the form can be paid. Nothing is submitted -- no payment attempt, so no
   * failure -- and the shopper is taken to the first thing still missing.
   *
   * The button stays live rather than disabled: a disabled pay button reads as broken on a screen
   * where most people who arrive never try to pay, and it gives no clue what it is waiting for.
   */
  const showWhatIsMissing = async () => {
    const missing = addressComplete ? 'payment' : 'address';
    trackNanoCheckoutConfirmIncomplete(properties, missing);

    await goToMissingFields(missing, { validate: true });
  };

  /**
   * Takes the shopper to the first card still missing something. Confirm on an unfilled form also marks
   * what is missing there; "Pay with card" does not, because a shopper who has just chosen the card form
   * and typed nothing yet would land on a wall of red fields.
   */
  const goToMissingFields = async (missing: 'address' | 'payment', { validate }: { validate: boolean }) => {
    // scroll first and only then validate and focus. Focusing a Stripe field (and `elements.submit()`,
    // which focuses the first bad one) jumps the page to it, and doing either mid-scroll cut the
    // smooth scroll short with a snap. Once the card has arrived, the field is already in view and
    // neither one moves the page.
    const target = missing === 'address' ? document.getElementById(SHIP_TO_CARD_ID) : paymentCardRef.current;
    if (target) {
      await smoothScrollTo(target);
    }

    // `elements.submit()` validates every mounted element and draws each one's errors inline, which
    // is exactly the "here is what is wrong" this needs. Only when the shopper is paying through the
    // element with a form to fill: with the saved card selected it would flag the dimmed card form
    // they are not using, and a wallet has nothing inline to flag.
    if (validate && elements && !payingWithCardOnFile && !paysThroughWalletSheet) {
      await elements.submit();
    }

    if (missing === 'address') {
      elements?.getElement('address')?.focus();
    } else {
      elements?.getElement('payment')?.focus();
    }
  };

  const onPayWithCard = () => {
    trackNanoCheckoutPayWithCardTapped(properties);
    void goToMissingFields(addressComplete ? 'payment' : 'address', { validate: false });
  };

  const onConfirm = () => {
    if (canSubmit) {
      void submit();
    } else {
      void showWhatIsMissing();
    }
  };

  const confirmsApplePay = !payingWithCardOnFile && stripeType === 'apple_pay';
  const confirmsGooglePay = !payingWithCardOnFile && stripeType === 'google_pay';

  const payBlock = (
    <>
      {/* One sentence next to the charge: what today buys, what happens after, and how to get out.
          The credit is deliberately not restated -- `OrderSummary` directly above already renders it
          as its own signed line, so the arithmetic is visible rather than claimed. Today's figure
          names the hardware as well as the months, because pricing only the membership let a reader
          assume the tracker was extra. The renewal figure comes from the offer, since it is a claim
          about what web will actually schedule.
          The renewal figure carries the only bold. Today's total is already the heaviest thing on the
          screen in OrderSummary's total row, so emphasising it again here says nothing; the renewal is
          the one fact in this sentence that appears nowhere else, and it is the part a skimmer must
          not miss. One bold, not two -- two in a 13px grey sentence reads striped and cancels out. */}
      <p className={styles.disclosure}>
        {formatCents(breakdown.totalInCents)} today covers the Fi Mini tracker and {petName}’s first 6 months
        {offer.renewalText ? (
          <>
            , then <strong>{offer.renewalText}</strong>
          </>
        ) : (
          ''
        )}{' '}
        — we’ll email you before it renews, and you can cancel any time from the app.
      </p>

      <div className={styles.ctaContainer}>
        {/* the wallet button stays mounted in the variant and in one place, so it reports `ready` once
            and keeps its state; when Confirm is the bar's action, `ctaFirst` lifts Confirm above it */}
        {expressWallets && (
          <ExpressWalletButton
            offer={offer}
            breakdown={breakdown}
            // on offer whenever the browser has a wallet: the bar's action while the form cannot pay in
            // one tap, and under Confirm once it can, so a saved-card shopper still has Apple Pay
            shown={walletAvailable}
            onAvailable={(available) => {
              setWalletAvailable(available);
              if (available && !walletShownTracked.current) {
                walletShownTracked.current = true;
                trackNanoCheckoutWalletAvailable(properties);
              }
            }}
            onTapped={onWalletTapped}
            disabled={submitting}
            onToken={submitWallet}
            onFailed={(reason) => trackNanoCheckoutFailed(properties, reason)}
            error={errorFrom === 'wallet' ? error : null}
          />
        )}

        {/* live until a purchase is in flight: an incomplete form is handled on tap by
            `showWhatIsMissing`, which marks the missing fields in the cards above */}
        {walletFirst ? (
          <button className={styles.ctaPayWithCard} type="button" onClick={onPayWithCard}>
            Pay with card
          </button>
        ) : confirmsApplePay ? (
          /* safari draws the apple pay mark itself via -webkit-appearance, per apple's mark rules;
             the button carries no text of its own */
          <button
            className={classNames(styles.ctaApplePay, styles.ctaFirst)}
            type="button"
            disabled={submitting}
            aria-label="Pay with Apple Pay"
            onClick={onConfirm}
          />
        ) : confirmsGooglePay ? (
          <button
            className={classNames(styles.ctaGooglePay, styles.ctaFirst)}
            type="button"
            disabled={submitting}
            onClick={onConfirm}
          >
            {submitting ? 'Confirming…' : 'Pay with Google Pay'}
          </button>
        ) : (
          <button
            className={classNames(styles.ctaPrimary, styles.ctaFirst)}
            type="button"
            disabled={submitting}
            onClick={onConfirm}
          >
            {submitting ? 'Confirming…' : 'Confirm'}
          </button>
        )}
      </div>

      {/* the exit sits at the foot of the page, under the sticky bar rather than in it. In the bar it
          was on screen from the first second, before the price or the renewal had been read, and
          most checkouts that did not buy went on to the chip list it leads to. The Back arrow at
          the top is still one tap away. */}
      <a
        className={classNames(styles.ctaGhost, styles.ctaExit)}
        href={Links.manageHome}
        onClick={() => trackNanoCheckoutNotNowTapped(properties)}
      >
        Not now
      </a>
    </>
  );

  return (
    <>
      <div className={classNames(styles.card, styles.scrollTarget)} ref={paymentCardRef}>
        <div className={styles.cardHeader}>
          <span className={styles.sectionTitle}>Payment</span>
        </div>

        {/* the saved recurly card, drawn as a radio row above the stripe options so the two read as
            one list. stripe cannot render it -- recurly owns the stored card -- so the row is ours. */}
        {cardOnFile && (
          <label className={styles.savedCardRow}>
            <input
              type="radio"
              name="nano-payment-method"
              className={styles.savedCardRadio}
              checked={payingWithCardOnFile}
              onChange={() => setMethod('saved')}
            />
            <span className={styles.addressLine}>
              {cardOnFile.brand} •••• {String(cardOnFile.lastFour).padStart(4, '0')} · Expires {cardOnFile.month}/
              {cardOnFile.year}
            </span>
          </label>
        )}

        {/* stays mounted while the saved card is selected -- apple pay has to be visible to be
            choosable -- but dims so the checked row above reads as the active choice.
            link must stay enabled here: stripe only runs the email lookup and OTP when link can
            actually pay, so suppressing it in this element disables link for the whole group and
            turns the email card above into a plain input */}
        <div className={classNames(payingWithCardOnFile && styles.paymentElementDimmed)}>
          <PaymentElement
            options={paymentElementOptions}
            onLoadError={() => setElementFailed(true)}
            onChange={(event: StripePaymentElementChangeEvent) => {
              const type = event.value.type;
              // the element fires a change on mount with its default selection; only a switch away
              // from that is the shopper choosing to pay through stripe over the saved card
              if (lastStripeType.current !== null && type !== lastStripeType.current) {
                setMethod('stripe');
              }
              lastStripeType.current = type;
              setStripeType(type);
              setElementComplete(event.complete);
            }}
            onFocus={() => setMethod('stripe')}
          />
        </div>
      </div>

      {error && errorFrom === 'form' && <div className={styles.fieldError}>{error}</div>}

      {/* on desktop these sit in the sticky summary column, beside the total; on a phone they stay
          here at the foot of the form */}
      {paySlot ? createPortal(payBlock, paySlot) : payBlock}
    </>
  );
}
